import React, { useEffect, useMemo, useState } from "react";

/**
 * Lightweight paginated table — avoids MUI DataGrid cost on large admin pages.
 * columns: { field, headerName, renderCell?(row), valueGetter?(row) }
 */
const AdminPaginatedTable = ({
  columns,
  rows,
  defaultPageSize = 10,
  pageSizeOptions = [5, 10, 20, 50],
  onRowClick,
  emptyMessage = "No data",
  /** When true, render all rows and hide client-side pager (use external/server pagination). */
  serverPaged = false,
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  useEffect(() => {
    if (!serverPaged) setPage(0);
  }, [rows.length, pageSize, serverPaged]);

  const pageCount = Math.max(1, Math.ceil(Math.max(rows.length, 1) / pageSize));
  const safePage = Math.min(page, pageCount - 1);

  const pageRows = useMemo(
    () => (serverPaged ? rows : rows.slice(safePage * pageSize, safePage * pageSize + pageSize)),
    [rows, safePage, pageSize, serverPaged]
  );

  const rangeStart = rows.length === 0 ? 0 : serverPaged ? 1 : safePage * pageSize + 1;
  const rangeEnd = rows.length === 0 ? 0 : serverPaged ? rows.length : Math.min(rows.length, (safePage + 1) * pageSize);

  const renderCellValue = (col, row) => {
    if (typeof col.renderCell === "function") return col.renderCell(row);
    if (typeof col.valueGetter === "function") return col.valueGetter(row);
    const value = row[col.field];
    return value == null || value === "" ? "—" : value;
  };

  return (
    <div className="min-w-0 rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm">
          <thead className="bg-slate-800 text-white">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.field}
                  className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                >
                  {col.headerName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-slate-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pageRows.map((row, index) => (
                <tr
                  key={row.id ?? `${row.date || row.orderId || "row"}-${index}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? "button" : undefined}
                  className={
                    onRowClick
                      ? "cursor-pointer border-t border-slate-200 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#38513b]/40"
                      : "border-t border-slate-200"
                  }
                >
                  {columns.map((col) => (
                    <td
                      key={col.field}
                      className="px-3 py-2.5 text-slate-700 align-middle whitespace-nowrap"
                    >
                      {renderCellValue(col, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!serverPaged ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-3 py-2 text-xs text-slate-600">
          <label className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <span>
            {rangeStart}-{rangeEnd} of {rows.length}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={safePage <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
              aria-label="Previous page"
            >
              ‹
            </button>
            <button
              type="button"
              disabled={safePage >= pageCount - 1 || rows.length === 0}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        </div>
      ) : onRowClick ? (
        <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
          Click a row for full transaction details.
        </p>
      ) : null}
    </div>
  );
};

export default AdminPaginatedTable;
